import { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";

import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  AuthError,
  signInWithRedirect,
  fetchUserAttributes,
  autoSignIn,
  updatePassword as amplifyUpdatePassword,
  deleteUser,
  updateUserAttribute,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
} from "aws-amplify/auth";
import type { AuthContextType } from "../types/AuthContextType";
import type { User } from "../types/User";

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // State
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sign-up state
  const [signUpEmail, setSignUpEmail] = useState<string | null>(null);
  const [requiresEmailConfirmation, setRequiresEmailConfirmation] =
    useState(false);

  // Tokens
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);

  const [userAttributes, setUserAttributes] = useState<Record<
    string,
    string
  > | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const loadUserAttributes = async () => {
    try {
      const attributes = await fetchUserAttributes();
      setUserAttributes(attributes as Record<string, string>);
    } catch (err) {
      console.error("Error fetching user attributes:", err);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn({
        username: email,
        password,
      });

      if (result.isSignedIn) {
        const session = await fetchAuthSession();
        const accessTokenString = session.tokens?.accessToken?.toString();
        const idTokenString = session.tokens?.idToken?.toString();
        const currentUser = await getCurrentUser();

        setAccessToken(accessTokenString || null);
        setIdToken(idTokenString || null);
        setUser({
          username: currentUser.username,
          userId: currentUser.userId,
        });

        await loadUserAttributes();
        setIsAuthenticated(true);
        window.location.href = "/profile";
      }
    } catch (err) {
      const authError = err as AuthError;

      let errorMessage = "Login failed";

      if (authError?.name === "NotAuthorizedException") {
        errorMessage = "Invalid email or password";
      } else if (authError?.name === "UserNotFoundException") {
        errorMessage = "User not found";
      } else if (authError?.name === "UserNotConfirmedException") {
        errorMessage = "Please verify your email first";
      } else if (authError?.name === "TooManyRequestsException") {
        errorMessage = "Too many login attempts. Please try again later";
      }

      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      console.error("Login error:", authError);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    phone: string,
    lastName: string
  ): Promise<{ requiresEmailConfirmation: boolean }> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: `${firstName} ${lastName}`,
            given_name: firstName,
            family_name: lastName,
            phone_number: phone,
          },
          autoSignIn: true,
        },
      });

      // Check if email confirmation is required
      if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setSignUpEmail(email);
        setRequiresEmailConfirmation(true);
        return { requiresEmailConfirmation: true };
      }

      // If auto sign-in is enabled
      if (result.isSignUpComplete) {
        const session = await fetchAuthSession();
        const accessTokenString = session.tokens?.accessToken?.toString();
        const idTokenString = session.tokens?.idToken?.toString();
        const currentUser = await getCurrentUser();

        setAccessToken(accessTokenString || null);
        setIdToken(idTokenString || null);
        setUser({
          username: currentUser.username,
          userId: currentUser.userId,
        });

        // ✅ Always fetch attributes explicitly
        await loadUserAttributes();
        setIsAuthenticated(true);
        setRequiresEmailConfirmation(false);
      }

      return { requiresEmailConfirmation: false };
    } catch (err) {
      const authError = err as AuthError;

      let errorMessage = "Sign up failed";

      if (authError?.name === "UsernameExistsException") {
        errorMessage = "This email is already registered";
      } else if (authError?.name === "InvalidPasswordException") {
        errorMessage =
          "Password does not meet requirements. Must include uppercase, lowercase, numbers, and special characters";
      } else if (authError?.name === "InvalidParameterException") {
        errorMessage = "Invalid input parameters. Please check your details";
      } else if (authError?.name === "CodeDeliveryFailureException") {
        errorMessage =
          "Failed to send verification email. Please try again later";
      } else if (authError?.name === "TooManyRequestsException") {
        errorMessage = "Too many sign-up attempts. Please try again later";
      }

      setError(errorMessage);
      setUser(null);
      setIsAuthenticated(false);
      console.error("Sign up error:", authError);

      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmEmail = async (confirmationCode: string): Promise<void> => {
    if (!signUpEmail) {
      throw new Error("No email found for confirmation");
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await confirmSignUp({
        username: signUpEmail,
        confirmationCode,
      });

      if (result.isSignUpComplete) {
        const autoResult = await autoSignIn();

        if (autoResult?.isSignedIn) {
          const session = await fetchAuthSession();
          const currentUser = await getCurrentUser();

          setAccessToken(session.tokens?.accessToken?.toString() || null);
          setIdToken(session.tokens?.idToken?.toString() || null);
          setUser({
            username: currentUser.username,
            userId: currentUser.userId,
          });

          await loadUserAttributes();
          setIsAuthenticated(true);
          setSignUpEmail(null);
          setRequiresEmailConfirmation(false);
          window.location.href = "/profile";
        }
      }
    } catch (err) {
      const authError = err as AuthError;

      let errorMessage = "Email confirmation failed";

      if (authError?.name === "CodeMismatchException") {
        errorMessage = "Invalid confirmation code. Please try again";
      } else if (authError?.name === "ExpiredCodeException") {
        errorMessage = "Confirmation code expired. Please request a new one";
      } else if (authError?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later";
      } else if (authError?.name === "UserNotFoundException") {
        errorMessage = "User not found. Please sign up again";
      }

      setError(errorMessage);
      console.error("Email confirmation error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * FEDERATED LOGIN
   * Sign in with third-party providers (Google, Apple)
   */
  const federatedLogin = async (
    provider: "Google" | "Apple"
  ): Promise<void> => {
    try {
      setIsRedirecting(true);

      await signInWithRedirect({ provider });
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || "Social login failed");
    }
  };

  const changeUserPassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Use the imported Amplify function with the alias
      await amplifyUpdatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      console.log("Password updated successfully");
    } catch (err) {
      const authError = err as AuthError;
      let errorMessage = "Password change failed";

      if (authError?.name === "NotAuthorizedException") {
        errorMessage = "Incorrect current password";
      } else if (authError?.name === "InvalidPasswordException") {
        errorMessage = "New password does not meet requirements";
      } else if (authError?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later";
      }

      setError(errorMessage);
      console.error("Password update error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ✅ UPDATE USER ATTRIBUTES
   * Update phone number or other user attributes
   */
  const updateUserAttributes = async (
    attributes: Record<string, string>
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      for (const [key, value] of Object.entries(attributes)) {
        const result = await updateUserAttribute({
          userAttribute: {
            attributeKey: key,
            value,
          },
        });

        console.log(`Updated ${key}:`, result);
      }

      // Reload attributes after update
      await loadUserAttributes();
    } catch (err) {
      const authError = err as AuthError;
      let errorMessage = "Failed to update user attributes";

      if (authError?.name === "InvalidParameterException") {
        errorMessage = "Invalid attribute parameters";
      } else if (authError?.name === "NotAuthorizedException") {
        errorMessage = "Not authorized to update attributes";
      }

      setError(errorMessage);
      console.error("Update attributes error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ✅ SEND ATTRIBUTE VERIFICATION CODE
   * Request verification code for phone number or email
   */
  const sendAttributeVerificationCode = async (
    attributeKey: "phone_number" | "email"
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await sendUserAttributeVerificationCode({
        userAttributeKey: attributeKey,
      });

      console.log(`Verification code sent to ${result}`);
    } catch (err) {
      const authError = err as AuthError;
      let errorMessage = "Failed to send verification code";

      if (authError?.name === "InvalidParameterException") {
        errorMessage = "Invalid attribute or user not found";
      } else if (authError?.name === "CodeDeliveryFailureException") {
        errorMessage = "Failed to deliver verification code";
      }

      setError(errorMessage);
      console.error("Send verification code error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ✅ VERIFY USER ATTRIBUTE
   * Confirm attribute with verification code
   */
  const verifyUserAttribute = async (
    attributeKey: "phone_number" | "email",
    confirmationCode: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await confirmUserAttribute({
        userAttributeKey: attributeKey,
        confirmationCode,
      });

      console.log(`${attributeKey} verified successfully`);

      // Reload attributes to reflect verification status
      await loadUserAttributes();
    } catch (err) {
      const authError = err as AuthError;
      let errorMessage = "Failed to verify attribute";

      if (authError?.name === "CodeMismatchException") {
        errorMessage = "Invalid verification code";
      } else if (authError?.name === "ExpiredCodeException") {
        errorMessage = "Verification code expired";
      } else if (authError?.name === "LimitExceededException") {
        errorMessage = "Too many verification attempts";
      }

      setError(errorMessage);
      console.error("Verify attribute error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * LOGOUT
   * Sign out from Cognito and clear local state
   */
  const logout = async (): Promise<void> => {
    try {
      await signOut();
      window.location.href = "/";
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
      setIdToken(null);
      setError(null);
      setSignUpEmail(null);
      setRequiresEmailConfirmation(false);
      setUserAttributes(null);
    }
  };

  const deleteProfile = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await deleteUser();

      // Clear all auth state after successful deletion
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
      setIdToken(null);
      setUserAttributes(null);
      setSignUpEmail(null);
      setRequiresEmailConfirmation(false);

      window.location.href = "/";
    } catch (err) {
      const authError = err as AuthError;
      let errorMessage = "Profile deletion failed";

      if (authError?.name === "NotAuthorizedException") {
        errorMessage = "You are not authenticated";
      } else if (authError?.name === "UserNotFoundException") {
        errorMessage = "User not found";
      } else if (authError?.name === "InvalidParameterException") {
        errorMessage = "Invalid parameters";
      }

      setError(errorMessage);
      console.error("Profile deletion error:", authError);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * CHECK SESSION ON APP LOAD
   * Restore user session and attributes after page reload
   */
  useEffect(() => {
    const checkSession = async () => {
      try {
        const session = await fetchAuthSession();

        if (session.tokens) {
          const currentUser = await getCurrentUser();

          setUser({
            username: currentUser.username,
            userId: currentUser.userId,
          });

          setAccessToken(session.tokens.accessToken?.toString() || null);
          setIdToken(session.tokens.idToken?.toString() || null);
          setIsAuthenticated(true);

          // ✅ Always fetch attributes explicitly on app load
          await loadUserAttributes();
        }
      } catch (err) {
        setIsAuthenticated(false);
      }
    };

    checkSession();
  }, []);

  /**
   * CLEAR ERROR
   * Clear error messages from state
   */
  const clearError = (): void => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    accessToken,
    idToken,
    signUpEmail,
    requiresEmailConfirmation,
    userAttributes,
    login,
    register,
    confirmEmail,
    federatedLogin,
    logout,
    clearError,
    changeUserPassword,
    deleteProfile,
    isRedirecting,
    setIsRedirecting,
    updateUserAttributes, // ✅ ADD THIS
    sendAttributeVerificationCode, // ✅ ADD THIS
    verifyUserAttribute, // ✅ ADD THIS
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
